import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { OpenAIBaseService } from "./open-ai-base.service";

interface Quote {
  text: string;
  author: string;
}

@Injectable({
  providedIn: "root",
})
export class DailyQuotesService extends OpenAIBaseService {
  private quotes: string[] = [
    "Design is not just what it looks like and feels like. Design is how it works. - Steve Jobs",
    "Good design is obvious. Great design is transparent. - Joe Sparano",
    "Design creates culture. Culture shapes values. Values determine the future. - Robert L. Peters",
    "The details are not the details. They make the design. - Charles Eames",
    "Design is thinking made visual. - Saul Bass",
    "Simplicity is the ultimate sophistication. - Leonardo da Vinci",
    "Design is where science and art break even. - Robin Mathew",
    "A designer knows he has achieved perfection not when there is nothing left to add, but when there is nothing left to take away. - Antoine de Saint-Exupéry",
    "Design is not for philosophy it's for life. - Issey Miyake",
    "The function of design is letting design function. - Micha Commeren",
  ];

  private currentQuoteSubject = new BehaviorSubject<Quote>({
    text: "",
    author: "",
  });
  public currentQuote$: Observable<Quote> =
    this.currentQuoteSubject.asObservable();

  constructor() {
    super();
    // Initialize with quote of the day
    this.currentQuoteSubject.next(this.getQuoteOfTheDayObject());
  }

  /**
   * Get a random inspirational design quote
   */
  getRandomQuote(): string {
    const randomIndex = Math.floor(Math.random() * this.quotes.length);
    return this.quotes[randomIndex];
  }

  /**
   * Parse a quote string into text and author
   */
  private parseQuote(quoteString: string): Quote {
    const parts = quoteString.split(" - ");
    if (parts.length >= 2) {
      return {
        text: parts[0].trim(),
        author: parts.slice(1).join(" - ").trim(),
      };
    }
    return {
      text: quoteString.trim(),
      author: "Unknown",
    };
  }

  /**
   * Get quote of the day as string (for backward compatibility)
   */
  private getQuoteOfTheDayString(): string {
    const today = new Date();
    const dayOfYear = Math.floor(
      (today.getTime() - new Date(today.getFullYear(), 0, 0).getTime()) /
        86400000
    );
    const quoteIndex = dayOfYear % this.quotes.length;
    return this.quotes[quoteIndex];
  }

  /**
   * Get quote of the day based on current date (consistent for the same day)
   */
  getQuoteOfTheDay(): string {
    return this.getQuoteOfTheDayString();
  }

  /**
   * Get quote of the day as Quote object
   */
  getQuoteOfTheDayObject(): Quote {
    return this.parseQuote(this.getQuoteOfTheDayString());
  }

  /**
   * Update the current quote (useful for refreshing or setting a new quote)
   */
  updateCurrentQuote(): void {
    this.currentQuoteSubject.next(this.getQuoteOfTheDayObject());
  }

  /**
   * Generate a personalized AI quote for a specific user
   */
  async generatePersonalizedQuote(userName?: string): Promise<string> {
    try {
      const prompt = `Generate an inspirational quote about design, creativity, or career growth${
        userName ? ` that could motivate someone named ${userName}` : ""
      }. Keep it under 150 characters and make it uplifting and professional. Do not include attribution unless it's a famous quote.`;

      const systemPrompt =
        "You are a motivational coach specializing in design careers. Generate short, inspiring quotes that encourage creativity and professional growth.";

      const content = await this.makeOpenAIChatRequest(
        prompt,
        systemPrompt,
        "gpt-4o-mini",
        0.7
      );

      // Clean up the response and ensure it's not too long
      const cleanQuote = content.replace(/^["']|["']$/g, "").trim();
      return cleanQuote.length > 150 ? this.getQuoteOfTheDay() : cleanQuote;
    } catch (error) {
      console.error("Error generating personalized quote:", error);
      // Fallback to static quote
      return this.getQuoteOfTheDay();
    }
  }

  /**
   * Get multiple quotes for variety
   */
  getMultipleQuotes(count: number): string[] {
    const shuffled = [...this.quotes].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, Math.min(count, this.quotes.length));
  }
}
